/*
 * Copyright 2025 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.feature.currencyrates.navigation

import androidx.navigation.NavController
import androidx.navigation.NavGraphBuilder
import androidx.navigation.NavOptions
import androidx.navigation.navigation
import kotlinx.serialization.Serializable
import kpt.core.base.ui.nav.FeatureDestination
import kpt.core.base.ui.nav.composableWithPushTransitions
import kpt.core.base.ui.nav.popBackStackSafely
import kpt.feature.currencyrates.ui.CurrencyRatesScreen
import kpt.feature.currencyrates.ui.RateHistoryScreen

/** Route for the currency-rates nested graph. */
@Serializable
data object CurrencyRatesGraphRoute

/** Route for the live rates list — the graph's start destination. */
@Serializable
data object CurrencyRatesRoute

/** Route for the historical chart. */
@Serializable
data object RateHistoryRoute

/**
 * Navigates to the rates list.
 *
 * @param navOptions optional nav options.
 */
fun NavController.navigateToCurrencyRates(navOptions: NavOptions? = null) {
    navigate(route = CurrencyRatesGraphRoute, navOptions = navOptions)
}

/**
 * Registers the currency-rates nested graph.
 *
 * @param navController used for back navigation and for reaching the history screen.
 */
@FeatureDestination
fun NavGraphBuilder.currencyRatesGraph(navController: NavController) {
    navigation<CurrencyRatesGraphRoute>(startDestination = CurrencyRatesRoute) {
        composableWithPushTransitions<CurrencyRatesRoute> {
            CurrencyRatesScreen(onBackClick = { navController.popBackStackSafely() })
        }

        composableWithPushTransitions<RateHistoryRoute> {
            RateHistoryScreen(onBackClick = { navController.popBackStackSafely() })
        }
    }
}

/**
 * Navigates to the historical chart.
 *
 * @param navOptions optional nav options.
 */
fun NavController.navigateToRateHistory(navOptions: NavOptions? = null) {
    navigate(route = RateHistoryRoute, navOptions = navOptions)
}
